/**
 * Construcción del objeto de metainformación que el widget envía en el campo
 * `seccion` de cada mensaje (texto o media). Ese mismo campo alimenta el prompt
 * IA `[UBICACIÓN EN LA APP]`, de modo que enriquecerlo da al agente contexto
 * automático del entorno del contacto (navegador, SO, path, zona horaria, etc.).
 *
 * Regla de oro: NUNCA romper el envío. Cada acceso a APIs del navegador
 * (`navigator`, `screen`, `Intl`, `window.location`) va envuelto
 * defensivamente; ante cualquier fallo se usa un default ("Desconocido"/null).
 */

/**
 * Shape del objeto de metainfo que viaja bajo la clave `seccion`.
 * Es un `type` (no `interface`) para que sea asignable a `Record<string, unknown>`,
 * el tipo del campo `seccion` del transporte.
 */
export type WidgetMetainfo = {
  origen: 'Widget-Web';
  path: string;
  navegador: string;
  so: string;
  idioma: string;
  zonaHoraria: string;
  resolucion: { pantalla: string; viewport: string };
  referrer: string | null;
  /** Valor legacy de `getSection`/`section` provisto por el host (si lo usa). */
  seccion?: string | Record<string, unknown>;
  /** Metadatos arbitrarios provistos por el host (`getExtra`/`extra`). */
  extra?: Record<string, unknown>;
};

const DESCONOCIDO = 'Desconocido';

/** Tipado mínimo de la API experimental `navigator.userAgentData`. */
interface UADataBrand {
  brand: string;
  version: string;
}
interface NavigatorUAData {
  brands?: UADataBrand[];
  platform?: string;
}

/** Navegadores "de plataforma" que no identifican al browser real. */
const UA_BRAND_IGNORE = /not.a.brand|chromium/i;

/** Resuelve `navegador` (ej. "Chrome 121") desde `userAgentData.brands` si existe. */
function navegadorDesdeUAData(uaData: NavigatorUAData): string | null {
  try {
    const brands = uaData.brands;
    if (!Array.isArray(brands) || brands.length === 0) return null;
    // Preferimos una marca "real" (no el placeholder "Not.A/Brand" ni "Chromium").
    const real = brands.find((b) => b?.brand && !UA_BRAND_IGNORE.test(b.brand));
    const chosen = real ?? brands.find((b) => b?.brand);
    if (!chosen?.brand) return null;
    const version = (chosen.version ?? '').split('.')[0];
    return version ? `${chosen.brand} ${version}` : chosen.brand;
  } catch {
    return null;
  }
}

/** Parseo pequeño de `navigator.userAgent` como fallback para el navegador. */
function navegadorDesdeUA(ua: string): string {
  try {
    // El orden importa: Edge/Opera se anuncian también como Chrome/Safari.
    const patterns: Array<{ name: string; re: RegExp }> = [
      { name: 'Edge', re: /Edg(?:e|A|iOS)?\/(\d+)/ },
      { name: 'Opera', re: /(?:OPR|Opera)\/(\d+)/ },
      { name: 'Firefox', re: /Firefox\/(\d+)/ },
      { name: 'Chrome', re: /Chrome\/(\d+)/ },
      { name: 'Safari', re: /Version\/(\d+).*Safari/ },
    ];
    for (const p of patterns) {
      const m = ua.match(p.re);
      if (m) return `${p.name} ${m[1]}`;
    }
    return DESCONOCIDO;
  } catch {
    return DESCONOCIDO;
  }
}

/** Parseo pequeño de `navigator.userAgent`/`platform` como fallback para el SO. */
function soDesdeUA(ua: string, platform: string): string {
  try {
    // iOS antes que macOS: iPad/iPhone pueden reportar "Mac" en algunos WebKit.
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/Win/i.test(ua) || /Win/i.test(platform)) return 'Windows';
    if (/Mac/i.test(ua) || /Mac/i.test(platform)) return 'macOS';
    if (/Linux/i.test(ua) || /Linux/i.test(platform)) return 'Linux';
    return DESCONOCIDO;
  } catch {
    return DESCONOCIDO;
  }
}

/** Normaliza el `platform` de userAgentData (ej. "macOS", "Windows") a nuestra taxonomía. */
function normalizarPlatform(platform: string): string {
  const p = platform.toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('win')) return 'Windows';
  if (p.includes('android')) return 'Android';
  if (p === 'ios' || p.includes('iphone') || p.includes('ipad')) return 'iOS';
  if (p.includes('linux') || p.includes('chrome os')) return 'Linux';
  return platform || DESCONOCIDO;
}

function resolverNavegadorYSo(): { navegador: string; so: string } {
  let navegador = DESCONOCIDO;
  let so = DESCONOCIDO;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const ua = nav?.userAgent ?? '';
  const platform = (nav as unknown as { platform?: string })?.platform ?? '';

  const uaData = (nav as unknown as { userAgentData?: NavigatorUAData })?.userAgentData;
  if (uaData) {
    navegador = navegadorDesdeUAData(uaData) ?? navegadorDesdeUA(ua);
    so = uaData.platform ? normalizarPlatform(uaData.platform) : soDesdeUA(ua, platform);
  } else {
    navegador = navegadorDesdeUA(ua);
    so = soDesdeUA(ua, platform);
  }
  return { navegador, so };
}

function resolverPath(): string {
  try {
    const l = window.location;
    return `${l.pathname}${l.search}${l.hash}`;
  } catch {
    return '';
  }
}

function resolverResolucion(): { pantalla: string; viewport: string } {
  let pantalla = DESCONOCIDO;
  let viewport = DESCONOCIDO;
  try {
    pantalla = `${screen.width}x${screen.height}`;
  } catch {
    /* screen no disponible */
  }
  try {
    viewport = `${window.innerWidth}x${window.innerHeight}`;
  } catch {
    /* window no disponible */
  }
  return { pantalla, viewport };
}

function resolverIdioma(): string {
  try {
    return navigator.language || DESCONOCIDO;
  } catch {
    return DESCONOCIDO;
  }
}

function resolverZonaHoraria(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DESCONOCIDO;
  } catch {
    return DESCONOCIDO;
  }
}

function resolverReferrer(): string | null {
  try {
    return document.referrer || null;
  } catch {
    return null;
  }
}

/**
 * Arma el objeto de metainfo que viaja en `seccion`. `seccionLegacy` es el valor
 * de `getSection`/`section` del host (se incluye bajo la clave `seccion` solo si
 * trae algo); `extra` son metadatos arbitrarios del host (se incluye solo si es
 * un objeto con contenido). Siempre devuelve un objeto válido con los campos
 * automáticos, incluso si el host no configura nada.
 */
export function buildMetainfo(
  seccionLegacy?: string | Record<string, unknown>,
  extra?: Record<string, unknown>,
): WidgetMetainfo {
  const { navegador, so } = resolverNavegadorYSo();

  const meta: WidgetMetainfo = {
    origen: 'Widget-Web',
    path: resolverPath(),
    navegador,
    so,
    idioma: resolverIdioma(),
    zonaHoraria: resolverZonaHoraria(),
    resolucion: resolverResolucion(),
    referrer: resolverReferrer(),
  };

  if (seccionLegacy !== undefined && seccionLegacy !== null && seccionLegacy !== '') {
    meta.seccion = seccionLegacy;
  }
  if (extra && typeof extra === 'object' && Object.keys(extra).length > 0) {
    meta.extra = extra;
  }

  return meta;
}
